import { Tracker } from '@spt-tracking/pulse-sdk';
import { ClientShortHandName } from '@snoam/mono-scc';
import { Enum } from 'typescript-string-enums';
import { IProductInfo, IUserInfo } from './CustomTypes';
import {
  getRedirectDataFromLocalStorage,
  ILocalStorageData,
  isProduction
} from './fastflowUtils';

export const SccPulseActivityType = Enum('Leave', 'Watch', 'Custom');
export type SccPulseActivityType = Enum<typeof SccPulseActivityType>;

export const TriggerActionType = Enum('Click', 'Swipe', 'Pinch');
export type TriggerActionType = Enum<typeof TriggerActionType>;

export const ElementActionType = Enum(
  'Show',
  'Delete',
  'Add',
  'Edit',
  'Register'
);
export type ElementActionType = Enum<typeof ElementActionType>;

export const ElementType = Enum('A', 'BUTTON', 'DIV');
export type ElementType = Enum<typeof ElementType>;

export function getPulseProviderId(
  clientShortHandName: keyof typeof ClientShortHandName | ''
) {
  if (!clientShortHandName) {
    return '';
  }

  const clientIds: { [key in ClientShortHandName]: string } = {
    AGR: 'agder',
    AP: 'aftenposten',
    ASB: 'bsnett',
    ASK: 'askoyvaeringen',
    BEDRIFT: 'schibsted', // Same as SCH
    BTI: 'bt',
    BYN: 'bygdanytt',
    DNB: 'aftenposten',
    DP: 'dinepenger',
    E24: 'dinepenger', // Same as DP
    FAK: 'lister',
    FVN: 'faedrelandsvennen',
    KVN: 'kvinesdal',
    LIN: 'lindesnes',
    MPL: 'aftenposten-magasin-pluss',
    SA: 'stavangeraftenblad',
    SCH: 'schibsted',
    STR: 'strilen',
    SYS: 'sysla',
    VG: 'vg',
    VNY: 'vestnytt'
  };

  return clientIds[clientShortHandName];
}

export const validHostNames: { [key in ClientShortHandName]: string } = {
  AGR: 'agder24',
  AP: 'aftenposten',
  ASB: 'bsnett',
  ASK: 'av-avis',
  BEDRIFT: 'c26fe5cd-cf59-4f0b-8940-4da042f6f69d', // Added for completeness, but should match on SCH
  BTI: 'bt',
  BYN: 'bygdanytt',
  DNB: 'aftenposten-dnb',
  DP: 'dinepenger',
  E24: 'ae75e2a5-885b-4f39-bb7d-e23ebb9c8923', // Added for completeness, but should match on DP
  FAK: 'lister24',
  FVN: 'fvn',
  KVN: 'kvinesdal24',
  LIN: 'l-a',
  MPL: 'magasinpluss',
  SA: 'aftenbladet',
  SCH: 'schibsted',
  STR: 'strilen',
  SYS: 'sysla',
  VG: 'vg',
  VNY: 'vestnytt'
};

export function clientShortHandNameFromHostName(
  hostname: string
): ClientShortHandName {
  return Object.keys(validHostNames).find(
    (it: keyof typeof ClientShortHandName) =>
      hostname.includes(`.${validHostNames[it] as string}`)
  ) as ClientShortHandName;
}

/*
* Removed until server tracking is working corretly.
export const pulseConfig = {
  collectorPath: 'bestilling/api/v2/ff/track',
  collectorUrl: isProduction()
    ? 'https://kundeweb.aftenposten.no'
    : 'https://kundewebtest.aftenposten.no'
};
*/

let sccTracker: any = null;
export function sccPulseTrackerInit(
  clientShorthandName: ClientShortHandName | ''
): any {
  if (!clientShorthandName) {
    const provider = getPulseProviderId(
      clientShortHandNameFromHostName(window.location.hostname) || 'SCH'
    );
    sccTracker = new Tracker(provider);
  } else {
    sccTracker = new Tracker(getPulseProviderId(clientShorthandName));
  }
}

export function sccPulseTrack(eventType: string, eventData: IPulseEventData) {
  if (sccTracker == null) {
    return;
  }
  if (!isProduction()) {
    eventData.deployStage = 'dev';
    eventData.deployTag = 'PulseUnicorn';
  }
  sccTracker.track(eventType, eventData);
}

export function sccPulseTrackPurchase(
  productInfo: IProductInfo,
  trackingInfo: ILocalStorageData,
  paymentType: string,
  subscriptionId: string,
  owningClientId: string
) {
  if (sccTracker == null) {
    return;
  }

  sccTracker.track(
    'trackerEvent',
    sccPulseBuildPurchaseData(
      productInfo,
      trackingInfo,
      paymentType,
      subscriptionId,
      owningClientId
    )
  );
}

export function sccPulseBuildPurchaseData(
  productInfo: IProductInfo,
  trackingInfo: Pick<
    ILocalStorageData,
    | 'utmCampaign'
    | 'utmMedium'
    | 'utmContent'
    | 'utmSource'
    | 'referer'
    | 'utmTerm'
  >,
  paymentType: string,
  subscriptionId: string,
  owningClientId: string
) {
  const pulseEvent: IPulseEventData = {
    name: 'Payment Receipt',
    object: {
      businessTransaction: {
        capturedAmount: productInfo.price,
        capturedVatAmount: 0,
        clientId: owningClientId,
        clientName: productInfo.owningClientName,
        currency: 'NOK',
        id: subscriptionId,
        items: [
          {
            id: productInfo.productId,
            name: productInfo.header,
            price: productInfo.price,
            type: 'Product'
          }
        ],
        payment: {
          id: subscriptionId,
          method: paymentType === 'STREX' ? 'Payment mobile' : 'Payment card',
          onFile: 'true',
          provider: paymentType,
          type: 'Payment'
        },
        subtotal: productInfo.price,
        tax: 0,
        total: productInfo.price,
        type: TargetType.Transaction
      },
      id: 'payment_confirmation',
      name: 'receipt',
      type: TargetType.Confirmation
    },
    origin: {
      campaign: trackingInfo.utmCampaign,
      channel: trackingInfo.utmMedium,
      content: trackingInfo.utmContent,
      source: trackingInfo.utmSource,
      'spt:custom': {
        'spt:referer': trackingInfo.referer
      },
      term: trackingInfo.utmTerm
    },
    provider: {
      component: 'schibsted-abo'
    },
    type: EventType.View
  };

  if (!isProduction()) {
    pulseEvent.deployStage = 'dev';
    pulseEvent.deployTag = 'PulseUnicorn';
    return pulseEvent;
  }
  return pulseEvent;
}

export function sccPulseTrackPageView(
  productInfo: IProductInfo,
  userInfo: IUserInfo,
  eventName: string,
  elements?: any
) {
  if (!productInfo || !eventName) {
    return;
  }

  const redirectData = getRedirectDataFromLocalStorage();
  if (sccTracker == null) {
    sccPulseTrackerInit(productInfo.owningClient);
    if (sccTracker == null) {
      return;
    }
  }

  if (userInfo && userInfo.spidId) {
    sccPulseUpdate({
      actor: {
        id: '' + userInfo.spidId,
        realm: 'spid.no'
      }
    });
  }

  sccPulseTrack(
    'trackerEvent',
    createPulsePageViewEvent(
      productInfo,
      userInfo,
      eventName,
      redirectData,
      elements
    )
  );
}

export function createPulsePageViewEvent(
  productInfo: IProductInfo,
  userInfo: IUserInfo,
  eventName: string,
  redirectData: ILocalStorageData,
  elements?: any
) {
  const eventNamePrefixed = 'FF ' + eventName;
  const { subscriptionConfigurationId } = productInfo;
  const hasPaymentMethods =
    (userInfo &&
      userInfo.paymentDetails &&
      userInfo.paymentDetails.length > 0) ||
    (userInfo &&
      userInfo.paymentMethods &&
      userInfo.paymentMethods.length > 0) ||
    false;
  const pulseEvent: IPulseEventData = {
    name: eventNamePrefixed,
    object: {
      businessTransaction: {
        id: '' + subscriptionConfigurationId,
        payment: {
          id: '' + subscriptionConfigurationId,
          onFile: '' + hasPaymentMethods,
          type: 'Sale'
        }
      },
      elements: elements || null,
      id: '' + subscriptionConfigurationId,
      name: eventNamePrefixed,
      type: TargetType.Form
    },
    origin: {
      campaign: redirectData && redirectData.utmCampaign,
      channel: redirectData && redirectData.utmMedium,
      content: redirectData && redirectData.utmContent,
      source: redirectData && redirectData.utmSource,
      'spt:custom': {
        'spt:referer': redirectData && redirectData.referer
      },
      term: redirectData && redirectData.utmTerm
    },
    provider: {
      component: 'schibsted-abo'
    },
    type: EventType.View
  };
  return pulseEvent;
}

export function sccPulseUpdate(updateData: IUpdateData) {
  if (sccTracker == null) {
    return;
  }
  sccTracker.update(updateData);
}

export function sccPulseUserLogin(userId: number) {}
export function sccPulseUserLogout() {}

export const EventType = Enum(
  'Accept',
  'Autobump',
  'Bump',
  'Create',
  'Delete',
  'Engagement',
  'Error',
  'Hide',
  'Launch',
  'Login',
  'Logout',
  'Notification',
  'Pause',
  'Play',
  'Purchase',
  'Refer',
  'Reject',
  'Renew',
  'Report',
  'Save',
  'Search',
  'Send',
  'Show',
  'Unsave',
  'Update',
  'View',
  'Watch'
);
export type EventType = Enum<typeof EventType>;

export const IntentType = Enum(
  'Accept',
  'Autobump',
  'Bump',
  'Confirm',
  'Create',
  'Delete',
  'Login',
  'Logout',
  'Open',
  'Close',
  'Hide',
  'Play',
  'Pause',
  'Purchase',
  'Share',
  'Rate',
  'Recommend',
  'Refer',
  'Reject',
  'Renew',
  'Report',
  'Save',
  'Show',
  'Unsave',
  'Send',
  'Update',
  'Dismiss'
);
export type IntentType = Enum<typeof IntentType>;

export const TargetType = Enum(
  'Application',
  'Article',
  'BusinessTransaction',
  'ClassifiedAd',
  'Content',
  'Confirmation',
  'Error',
  'Form',
  'Listing',
  'Message',
  'NotificationContent',
  'Page',
  'PhoneContact',
  'PremiumFeature',
  'Product',
  'Rating',
  'Settings',
  'Transaction',
  'UIElement'
);
export type TargetType = Enum<typeof TargetType>;

export type EventDataActivity =
  | 'trackContentView'
  | 'trackCreate'
  | 'trackMediaState'
  | 'trackExit'
  | 'trackEngagement'
  | 'trackCustom'
  | 'trackBasic';

export interface IUpdateData {
  actor: {
    id: string;
    realm: string;
  };
}

export interface IPulsePurchaseData {
  price: number;
  clientId: string;
  clientName: string;
  subScriptionId: string;
  productId: string;
  productName: string;
  onFile: string;
  paymentType: string;
  utm: IPulseEventOrigin;
}

export interface IPulseEventData {
  type: EventType;
  name: string;
  object: IPulseEventObject;
  origin?: IPulseEventOrigin;
  provider?: IPulseEventProvider;
  deployStage?: string;
  deployTag?: string;
}

export interface IPulseEventObject {
  businessTransaction?: IPulseEventObjectTransaction;
  elements?: any;
  id: string;
  type: TargetType;
  name: string;
  url?: string;
}

export interface IPulseEventObjectTransaction {
  capturedAmount?: number;
  capturedVatAmount?: number;
  id: string;
  type?: TargetType;
  clientId?: string;
  clientName?: string;
  currency?: string;
  items?: object[];
  payment?: IPulseEventObjectTransactionPayment;
  subtotal?: number;
  tax?: number;
  total?: number;
}
export interface IPulseEventProvider {
  id?: string;
  type?: string;
  component?: string;
}
export interface IPulseEventObjectTransactionPayment {
  id: string;
  type: string;
  method?: string;
  onFile?: string;
  provider?: string;
}

export interface IPulseEventOrigin {
  url?: string;
  campaign?: string;
  channel?: string;
  content?: string;
  source?: string;
  term?: string;
  'spt:custom'?: object;
  referer?: string;
}

export interface IEventData {
  trackContentView?: [/*title?: */ string];
  trackCreate?: [
    /* objectType: */ string,
    /* objectId: */ string,
    /* meta?: */ object
  ];
  trackMediaState?: [
    /* mediaId: */ string,
    /* mediaType: */ 'video' | 'audio' | 'album',
    /* activityType?: */ SccPulseActivityType
  ];
  trackExit?: [
    /* targetId?: */ string,
    /* targetType?: */ string,
    /* activityType?: */ SccPulseActivityType
  ];
  trackEngagement?:
    | [
        /* duration: */ number,
        /* scrollPosition: */ string,
        /* triggerAction: */ TriggerActionType,
        /* elementType: */ ElementType,
        /* elementId: */ string,
        /* elementAction: */ ElementActionType,
        /* objectMeta?: */ { [k: string]: string } | string
      ]
    | [
        /* duration: */ number,
        /* scrollPosition: */ string,
        /* triggerAction: */ TriggerActionType,
        /* elementType: */ ElementType,
        /* elementId: */ string,
        /* elementAction: */ ElementActionType
      ];
  trackCustom?: [
    /* activityType: */ SccPulseActivityType,
    /* customData: */ { [k: string]: string }
  ];
  trackBasic?: [
    /* eventType: */ EventType,
    /* targetType: */ TargetType,
    /* targetId: */ string,
    /* objectMeta?: */ { [k: string]: string },
    /* intent */ IntentType
  ];
}



// WEBPACK FOOTER //
// ./src/PulseUtils.ts